import apiV2 from '../api/v2';
import { toApiError } from './apiErrors';

// Backed by the admin-only /api/v2/users/:userId/settings routes.
// Keep DEFAULTS in sync with backend's v2/shared/settings-allowlist.ts ALLOWED_SETTINGS.

export type UserSettingsMap = Record<string, Record<string, string>>;

interface SettingsDefaults {
    [category: string]: Record<string, boolean>;
}

const DEFAULTS: SettingsDefaults = {
    notifications: {
        chat_activity: false,
        digest_emails: true
    }
};

interface UserSettingRecord {
    id: string;
    userId: string;
    category: string;
    key: string;
    value: string;
}

function parseValue(raw: string | undefined, fallback: boolean): boolean {
    if (raw === undefined) return fallback;
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function toSettingsMap(records: UserSettingRecord[]): UserSettingsMap {
    const map: UserSettingsMap = {};
    for (const record of records) {
        map[record.category] = { ...map[record.category], [record.key]: record.value };
    }
    return map;
}

export function getSettingValue(settings: UserSettingsMap, category: string, key: string): boolean {
    const raw = settings[category]?.[key];
    const fallback = DEFAULTS[category]?.[key] ?? false;
    return parseValue(raw, fallback);
}

export async function fetchUserSettings(userId: string): Promise<UserSettingsMap> {
    try {
        const response = await apiV2.get<{ success: boolean; data: UserSettingRecord[] }>(
            `/users/${userId}/settings`
        );
        return toSettingsMap(response.data.data);
    } catch (err) {
        throw toApiError(err);
    }
}

export async function updateUserSetting(
    userId: string,
    category: string,
    key: string,
    value: boolean
): Promise<void> {
    try {
        await apiV2.put(`/users/${userId}/settings`, {
            category,
            key,
            value: JSON.stringify(value)
        });
    } catch (err) {
        throw toApiError(err);
    }
}
